import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signUp } from "../lib/auth-client.ts";
import { trackSignup } from "../lib/analytics.ts";
import { AuthDivider, GoogleButton } from "../components/GoogleButton.tsx";
import { PasswordField } from "../components/PasswordField.tsx";
import { ResendVerificationButton } from "../components/ResendVerificationButton.tsx";
import { AUTH_COPY, authResultError, normalizeAuthError, type AuthBanner } from "../lib/auth-messages.ts";

export function SignupPage() {
  const navigate = useNavigate();
  // Preserve a post-auth destination (e.g. an /accept-invitation link).
  const next = new URLSearchParams(window.location.search).get("next");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<AuthBanner | null>(null);
  const [verificationNeeded, setVerificationNeeded] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setVerificationNeeded(false);
    setLoading(true);
    try {
      const res = await signUp.email({
        name,
        email,
        password,
        callbackURL: `${window.location.origin}${next || "/dashboard"}`,
      });
      const message = authResultError(res, "Sign up failed. Please try again.");
      if (message) {
        setError(message);
        return;
      }
      const user = (res as { data?: { user?: { emailVerified?: boolean } | null } } | undefined)?.data?.user;
      if (user?.emailVerified) {
        trackSignup("email");
        navigate(next || "/dashboard", { replace: true });
        return;
      }
      trackSignup("email");
      setVerificationNeeded(true);
      setNotice({ kind: "info", text: AUTH_COPY.signupVerificationSent });
    } catch (err) {
      setError(normalizeAuthError(err, "Sign up failed. Please try again."));
    } finally {
      setLoading(false);
    }
  }


  return (
    <div className="auth-page">
      <div className="auth-wrap animate-fade-in">
        <div className="auth-logo">
          <div className="auth-logo-mark">
            <img src="/logo.webp" alt="CurrencyCore" />
          </div>
          <h1>Create an account</h1>
          <p>Start building with CurrencyCore</p>
        </div>

        <div className="auth-card">
          {error && <div className="auth-banner auth-banner-error">{error}</div>}
          {notice && <div className={`auth-banner auth-banner-${notice.kind}`}>{notice.text}</div>}

          <GoogleButton label="Sign up with Google" onError={setError} />
          <AuthDivider />

          <form onSubmit={handleSubmit} className="form-stack">
            <div className="form-group">
              <label htmlFor="name" className="form-label">Full name</label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-input"
                placeholder="Jane Smith"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setVerificationNeeded(false);
                  setNotice(null);
                }}
                className="form-input"
                placeholder="you@example.com"
              />
              {verificationNeeded && (
                <ResendVerificationButton
                  email={email}
                  callbackURL={`${window.location.origin}/dashboard`}
                  onError={setError}
                  onSuccess={(text) => {
                    setVerificationNeeded(true);
                    setNotice({ kind: "success", text });
                  }}
                />
              )}
            </div>

            <PasswordField
              id="password"
              label="Password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 8 characters"
            />

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-block"
            >
              {loading ? (
                <>
                  <span className="spinner" />
                  Creating account…
                </>
              ) : (
                "Create account"
              )}
            </button>
          </form>

          <p className="auth-tos">
            By signing up you agree to our{" "}
            <a href="https://currency-core.com/terms" target="_blank" rel="noreferrer">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="https://currency-core.com/privacy" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>.
          </p>
        </div>

        <p className="auth-footer">
          Already have an account?{" "}
          <Link to={next ? `/login?next=${encodeURIComponent(next)}` : "/login"}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
